import { SUBSCRIPTION_BILLING_CYCLES, SUBSCRIPTION_LEVELS } from '@sdk/account/constants';
import addDays from '@sdk/utils/addDays';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import type { BillingInfoType } from '@sdk/account/api/AccountApi';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';

import { FeatureNames } from './types';
import { FEATURE_ACCESS } from './constants';

export const YEAR_MONTHS = 12.0;

export const getFeatureAccessLevel = (
  featureName: FeatureNames,
  orgSettings: OrgSettings
): number => {
  const featureToAccess = (featureName) =>
    orgSettings.premiumFeatures[featureName] || SUBSCRIPTION_LEVELS.FREE;
  return featureName in FEATURE_ACCESS ? FEATURE_ACCESS[featureName] : featureToAccess(featureName);
};

export const getSlingFeatures = (
  orgSettings: OrgSettings,
  isOldPremium: boolean
): { [feature: string]: number } => {
  const features = {
    'Shift scheduling': getFeatureAccessLevel(FeatureNames.shiftScheduling, orgSettings),
    'Shift templates': getFeatureAccessLevel(FeatureNames.shiftTemplates, orgSettings),
    'Shift alarms': getFeatureAccessLevel(FeatureNames.alarms, orgSettings),
    'Shift offers': getFeatureAccessLevel(FeatureNames.shiftOffers, orgSettings),
    'Available shifts': getFeatureAccessLevel(FeatureNames.availableShifts, orgSettings),
    'Time off requests': getFeatureAccessLevel(FeatureNames.timeOff, orgSettings),
    Unavailability: getFeatureAccessLevel(FeatureNames.unavailability, orgSettings),
    'Private messaging': getFeatureAccessLevel(FeatureNames.privateMessages, orgSettings),
    'Group messaging': getFeatureAccessLevel(FeatureNames.groupMessages, orgSettings),
    Announcements: getFeatureAccessLevel(FeatureNames.announcements, orgSettings),
    'News sharing': getFeatureAccessLevel(FeatureNames.newsfeed, orgSettings),
    'Long term scheduling': getFeatureAccessLevel(FeatureNames.longTermScheduling, orgSettings),
    'Unlimited employees': getFeatureAccessLevel(FeatureNames.unlimitedEmployees, orgSettings),
    'Multiple locations': getFeatureAccessLevel(FeatureNames.multipleLocations, orgSettings),
    'Mobile time clock': getFeatureAccessLevel(FeatureNames.clockIn, orgSettings),
    'Timesheet approvals': getFeatureAccessLevel(FeatureNames.clockIn, orgSettings),
    'Early clock-in prevention': getFeatureAccessLevel(FeatureNames.clockIn, orgSettings),
    'Auto clock-out': getFeatureAccessLevel(FeatureNames.clockIn, orgSettings),
    Geofencing: getFeatureAccessLevel(FeatureNames.clockIn, orgSettings),
    'Shift swaps': getFeatureAccessLevel(FeatureNames.shiftSwaps, orgSettings),
    'Slots on available shifts': getFeatureAccessLevel(FeatureNames.shiftSlots, orgSettings),
    'Labor cost management': getFeatureAccessLevel(FeatureNames.laborCost, orgSettings),
    Budgeting: getFeatureAccessLevel(FeatureNames.laborCost, orgSettings),
    'Overtime tracking': getFeatureAccessLevel(FeatureNames.laborCost, orgSettings),
    'Holiday pay': getFeatureAccessLevel(FeatureNames.laborCost, orgSettings),
    'Calendar apps synchronization': getFeatureAccessLevel(
      FeatureNames.googleCalendarSync,
      orgSettings
    ),
    'Day parts': getFeatureAccessLevel(FeatureNames.dayParts, orgSettings),
    'SMS shift alarms': getFeatureAccessLevel(FeatureNames.smsShiftAlarms, orgSettings),
    'Kiosk time clock': getFeatureAccessLevel(FeatureNames.kiosk, orgSettings),
    'Auto-assigning': getFeatureAccessLevel(FeatureNames.autoAssigning, orgSettings),
    Reports: getFeatureAccessLevel(FeatureNames.reports, orgSettings),
    'Payroll reports': getFeatureAccessLevel(FeatureNames.reports, orgSettings),
    'Labor reports': getFeatureAccessLevel(FeatureNames.reports, orgSettings),
    'Sales reports': getFeatureAccessLevel(FeatureNames.reports, orgSettings),
    'No shows': getFeatureAccessLevel(FeatureNames.noShow, orgSettings),
    'Sick callouts': getFeatureAccessLevel(FeatureNames.sickCallout, orgSettings),
    'Late arrivals': getFeatureAccessLevel(FeatureNames.lateArrival, orgSettings),
    'Salary support': getFeatureAccessLevel(FeatureNames.salaries, orgSettings),
    'Newsfeed page settings': getFeatureAccessLevel(FeatureNames.newsfeedPageSettings, orgSettings),
    'Conversation settings': getFeatureAccessLevel(FeatureNames.conversationSettings, orgSettings),
    'PTO tracking': getFeatureAccessLevel(FeatureNames.leaveTypes, orgSettings),
    Tags: getFeatureAccessLevel(FeatureNames.tags, orgSettings),
    'Shift tasks': getFeatureAccessLevel(FeatureNames.shiftTasks, orgSettings),
    'Task templates': getFeatureAccessLevel(FeatureNames.shiftTasks, orgSettings),
    'Shift acceptance': getFeatureAccessLevel(FeatureNames.shiftAcceptance, orgSettings),
    'Dedicated account management': getFeatureAccessLevel(
      FeatureNames.dedicatedAccountManagement,
      orgSettings
    ),
    'Mobile time tracking': getFeatureAccessLevel(FeatureNames.clockIn, orgSettings),
    Timesheets: getFeatureAccessLevel(FeatureNames.clockIn, orgSettings),
    'Timesheet history': getFeatureAccessLevel(FeatureNames.clockIn, orgSettings),
    'Kiosk time tracking': getFeatureAccessLevel(FeatureNames.kiosk, orgSettings),
    'Attendance reports': getFeatureAccessLevel(FeatureNames.reports, orgSettings),
    Clopening: getFeatureAccessLevel(FeatureNames.clopening, orgSettings),
    'Schedule templates': getFeatureAccessLevel(FeatureNames.scheduleTemplates, orgSettings),
  };
  /* eslint-enable quote-props, key-spacing, dot-notation */

  if (isOldPremium) {
    const castForOldPremiums = (level) =>
      level === SUBSCRIPTION_LEVELS.OLD_PREMIUM ? SUBSCRIPTION_LEVELS.PREMIUM : level;

    Object.entries(features).forEach(([key, value]) => {
      if (value < 3) {
        features[key] = castForOldPremiums(value);
      }
    });
  }

  return features;
};

export const getBillingDate = (billingInfo: BillingInfoType, currentUser: AuthUserType): Date => {
  if (billingInfo.nextChargeDate) {
    return billingInfo.nextChargeDate;
  }

  // TODO I don't think that's necessarily true... because currentUser.orgSettings.premiumTrialLength
  // is not an actual trial lenghth, just a potential default (which it isn't due to some stuff on the backend),
  // so it might be replaced with billingInfo.trialDays
  const firstBillingAfterTrial = addDays(
    billingInfo.premiumTrialStartDate,
    currentUser.orgSettings.premiumTrialLength
  );
  const now = new Date();

  if (firstBillingAfterTrial.getTime() < now.getTime()) {
    return now;
  }

  return firstBillingAfterTrial;
};

export const getPricePerUnit = ({
  level,
  isYearlyPlan = false,
  orgSettings,
}: {
  level: number;
  isYearlyPlan?: boolean;
  orgSettings: OrgSettings;
}): string => {
  const cycle = isYearlyPlan ? 'yearly' : 'monthly';
  return (
    {
      monthly: {
        [SUBSCRIPTION_LEVELS.PREMIUM]: orgSettings.pricePerUser,
        [SUBSCRIPTION_LEVELS.BUSINESS]: orgSettings.businessPricePerUser,
      },
      yearly: {
        [SUBSCRIPTION_LEVELS.PREMIUM]: orgSettings.pricePerUserYearly,
        [SUBSCRIPTION_LEVELS.BUSINESS]: orgSettings.businessPricePerUserYearly,
      },
    }[cycle][level] || orgSettings.pricePerUser
  );
};

export const getYearlyDiscountPercentage = (level: number, orgSettings: OrgSettings): number => {
  const monthlyPricePerUserPerMonth = Number(getPricePerUnit({ level, orgSettings }));
  const yearlyPricePerUserPerMonth =
    Number(getPricePerUnit({ level, orgSettings, isYearlyPlan: true })) / YEAR_MONTHS;

  return Math.round(
    (100 * (monthlyPricePerUserPerMonth - yearlyPricePerUserPerMonth)) / monthlyPricePerUserPerMonth
  );
};

export function getIsYearlyPlan(subscriptionBillingCycle: SUBSCRIPTION_BILLING_CYCLES) {
  return subscriptionBillingCycle === SUBSCRIPTION_BILLING_CYCLES.ANNUAL;
}
